import React, {useCallback, useEffect, useRef, useState} from 'react'
import Button from "./Button";
import PropTypes from "prop-types";
import Input from "./Input";
import {useForm} from "../../hooks/form/formHook";
import DateTimePicker from "./DateTimePicker";
import Select from "./Select";
import {useTranslation} from "react-i18next";
import {get, isEmpty} from "lodash";
import {stringToDate} from "../../utils/DateUtils";
import Fragment from "./Fragment";
import Checkbox from "./Checkbox";
import Currency from "./Currency";
import {closestParentByClass} from "../../utils/DomUtils";
import useUpdateOnlyEffect from "../../hooks/effect/useUpdateOnlyEffect";

const TableFilter = (props) => {
  const {t} = useTranslation();
  const [filterOpen, setFilterOpen] = useState(false);
  const [filter, setFilter] = useState([]);

  const drawerElement = useRef({
    querySelector: () => {
    }
  });

  const {values, setValue, setFormNotDirty, getField} = useForm();

  const onOutsideFilterClick = useCallback((event) => {
    if(drawerElement.current.classList.contains("open") && closestParentByClass("table-filter", event.target) === null) {
      setFilterOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("click", onOutsideFilterClick);
    return () => document.removeEventListener("click", onOutsideFilterClick);
  }, []);

  useEffect(() => {
    // autofocus first input in the drawer
    filterOpen && drawerElement.current.querySelector(".first-filter .field:not(.dropdown) input")?.focus();
  }, [filterOpen]);

  useUpdateOnlyEffect(() => {
    const filter = props.columns.reduce((filter, column) => {
      const value = get(values, column.id);
      let from = value?.from;
      let to = value?.to;

      if (!Number.isInteger(value) && typeof value !== "boolean" && isEmpty(value)) { //isEmpty is returning true for integers and booleans
        return filter;
      }

      switch (column.type) {
        case "string":
        case "numericString":
          filter.push({
            name: column.id,
            operator: "LIKE",
            value: value,
          });
          break;
        case "select":
          if (String(value) === "null") {
            filter.push({
              name: column.id,
              operator: "ISNULL"
            });
            break;
          }
          filter.push({
            name: column.id,
            operator: "EQ",
            value: value
          });
          break;
        case "date":
        case "datetime":
          if (isEmpty(from) && isEmpty(to)) {
            break;
          }

          stringToDate(from) > stringToDate(to) && ([to, from] = [from, to]); //reverse variable values
          if (!isEmpty(from)) {
            filter.push({
              name: column.id,
              operator: "GTE",
              value: from
            });
          }
          if (!isEmpty(to)) {
            filter.push({
              name: column.id,
              operator: "LTE",
              value: to
            });
          }
          break;
        case "number":
        case "currency":
          if (from == null && to == null) {
            break;
          }

          parseFloat(from) > parseFloat(to) && ([to, from] = [from, to]); //reverse variable values
          if (from != null) {
            filter.push({
              name: column.id,
              operator: "GTE",
              value: from
            });
          }
          if (to != null) {
            filter.push({
              name: column.id,
              operator: "LTE",
              value: to
            });
          }
          break;
        case "boolean":
          filter.push({
            name: column.id,
            operator: "EQ",
            value: String(value)
          })
          break;
        default:
          return {};
      }
      return filter;
    }, []);
    setFilter(filter);
    props.onFilter && props.onFilter(filter);
  }, [values]);

  useEffect(() => {
    // only update filter when existing filter is empty (this means we are returned to the page)
    if (props.filter && props.filter.length > 0 && filter.length === 0) {
      const columnMap = props.columns.reduce((map, column) => {
        map[column.id] = column;
        return map;
      }, {});

      props.filter.forEach(filter => {
        const column = columnMap[filter.name];
        let value;
        if (!column) {
          return;
        }

        switch (column.type) {
          case "string":
          case "numericString":
            value = filter.value.replace(/^'+|'+$/gm, "").replace(/\\'/gm, "'");
            values[column.id] !== value && setValue(column.id, value);
            break;
          case "select":
            filter.operator === "EQ" && setValue(column.id, filter.value);
            break;
          case "date":
          case "datetime":
            if (filter.operator === "GTE") {
              setValue(column.id + ".from", filter.value);
            } else if (filter.operator === "LTE") {
              setValue(column.id + ".to", filter.value);
            }
            break;
          case "number":
          case "currency":
            if (filter.operator === "GTE") {
              setValue(column.id + ".from", filter.value);
            } else if (filter.operator === "LTE") {
              setValue(column.id + ".to", filter.value);
            }
            break;
          case "boolean":
            filter.operator === "EQ" && setValue(column.id, filter.value === "true");
            break;
          default:
            return {};
        }
      });
    }
  }, [props.filter]);

  const exportData = (filter) => {
    props.onExport && props.onExport(filter);
  }

  return (
    <div className="table-filter ignore-loading">
      <Fragment rendered={!!props.onExport}>
        <Button className="fz fz-file-export large" isOutlined={true} isLoading={props.isExporting} onClick={() => exportData(filter)}/>
      </Fragment>
      <div className="is-relative is-inline-flex">
        <Button className="fz fz-filter1 large marginless filter-button" color={props.filter.length > 0 ? "primary" : "default"} color={"primary"}
                isOutlined={true} disabled={props.disabled} onClick={() => setFilterOpen(!filterOpen)}/>
      </div>
      <div ref={drawerElement} className={filterOpen ? "table-filter-drawer card open" : "table-filter-drawer card"}>
        <div className="card-header-title">
          <span className="flex-grow-1 is-size-4">{t("Filter")}</span>
          <Button className="fz fz-times marginless" color="danger" onClick={() => setFilterOpen(false)} isOutlined={true}/>
        </div>
        <div className="card-content">
          {props.columns.map((column, index) =>
            <div key={column.id} className={index === 0 ? "columns first-filter" : "columns"}>
              {
                column.type === "string" &&
                <div className="column">
                  <Input label={t(column.name)} formField={getField(column.id)}/>
                </div>
              }
              {
                column.type === "numericString" &&
                <div className="column">
                  <Input label={t(column.name)} formField={getField(column.id)} type="number"/>
                </div>
              }
              {
                column.type === "date" &&
                <React.Fragment>
                  <div className="column">
                    <DateTimePicker label={t(column.name + "_from")} formField={getField(column.id + ".from")}/>
                  </div>
                  <div className="column">
                    <DateTimePicker label={t(column.name + "_to")} formField={getField(column.id + ".to")}/>
                  </div>
                </React.Fragment>
              }
              {
                column.type === "datetime" &&
                <React.Fragment>
                  <div className="column">
                    <DateTimePicker label={t(column.name + "_from")} formField={getField(column.id + ".from")} type="datetime"/>
                  </div>
                  <div className="column">
                    <DateTimePicker label={t(column.name + "_to")} formField={getField(column.id + ".to")} type="datetime"/>
                  </div>
                </React.Fragment>
              }
              {
                column.type === "currency" &&
                <React.Fragment>
                  <div className="column">
                    <Currency label={t(column.name + "_from")} formField={getField(column.id + ".from")}/>
                  </div>
                  <div className="column">
                    <Currency label={t(column.name + "_to")} formField={getField(column.id + ".to")}/>
                  </div>
                </React.Fragment>
              }
              {
                column.type === "number" &&
                <React.Fragment>
                  <div className="column">
                    <Input type="number" label={t(column.name + "_from")} formField={getField(column.id + ".from")}/>
                  </div>
                  <div className="column">
                    <Input type="number" label={t(column.name + "_to")} formField={getField(column.id + ".to")}/>
                  </div>
                </React.Fragment>
              }
              {
                column.type === "select" &&
                <div className="column">
                  <Select label={t(column.name)} formField={getField(column.id)} options={column.options} optionsLabel={column.optionsLabel} emptyOption={true}/>
                </div>
              }
              {
                column.type === "boolean" &&
                <div className="column">
                  <Checkbox label={t(column.name)} formField={getField(column.id)} type="form" color="auto" threeStateMode={true}/>
                </div>
              }
            </div>
          )}

          <Button className="fz fz-trash-alt" label={t("remove_filters")} color="danger" isOutlined={true} onClick={() => setFormNotDirty(true)} disabled={props.filter.length === 0}/>
        </div>
      </div>
    </div>
  );
}

TableFilter.propTypes = {
  columns: PropTypes.array.isRequired,
  filter: PropTypes.array.isRequired,
  onFilter: PropTypes.func.isRequired,
  onExport: PropTypes.func,
  isExporting: PropTypes.bool,
  disabled: PropTypes.bool,
};


export default TableFilter
